import React, { useState, useEffect, useCallback } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import type { Color } from '@atlassian/jira-issue-epic-color/src/common/types.tsx';
import type { ParentFieldOption } from '@atlassian/jira-issue-field-parent/src/common/types.tsx';
import { useParentField } from '@atlassian/jira-issue-field-parent/src/services/parent-field/index.tsx';
import { PARENT_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { toIssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { CellWrapper } from '../../../common/cell-wrapper/index.tsx';
import type { TransformedParentField } from '../../../common/field-types/parent/types.tsx';
import type { FieldCellProps, BasicListViewItem } from '../../../common/types.tsx';
import { ParentEdit } from './edit/async.tsx';
import { ParentRead } from './read/index.tsx';

const transformFieldToParent = (
	field: TransformedParentField | undefined | null,
): ParentFieldOption | null => {
	if (
		field?.value?.summary == null ||
		field?.value?.issueType == null ||
		field?.value?.issueId == null ||
		field?.value?.issueKey == null
	) {
		return null;
	}
	return {
		key: field?.value.issueKey,
		value: field?.value?.issueId,
		label: field?.value?.summary,
		fields: {
			summary: field?.value?.summary,
			issuetype: field?.value?.issueType,
		},
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		color: field?.value?.color as Color,
	};
};

export const ParentCell = ({
	rowData: issue,
	cellData: field,
	column: { width: preferredWidth },
	editMode,
	setEditMode,
	onIssueUpdated,
	cellRef,
}: FieldCellProps<BasicListViewItem, TransformedParentField>) => {
	const [parent, setParent] = useState(transformFieldToParent(field));

	const [{ value }] = useParentField({
		issueKey: toIssueKey(issue.key),
		fieldKey: field?.fieldId ?? '',
		issueId: issue.issueId,
	});

	if (value && value !== parent && fg('jwm_list_new_copy_paste_behaviour')) {
		setParent(value);
	}

	// Updated the value when a new updated is receive it
	useEffect(() => {
		setParent(transformFieldToParent(field));
	}, [field]);

	const handleChange = useCallback(
		(newParent: ParentFieldOption | null) => {
			setParent((oldParent) => {
				if (field != null) {
					onIssueUpdated({
						fieldId: field.fieldId,
						fieldType: field.type,
						issueId: issue.issueId,
						newValue: newParent,
						oldValue: oldParent,
						issueKey: toIssueKey(issue.key),
					});
				}

				return newParent;
			});
			setEditMode(false);
		},
		[field, issue.issueId, issue.key, onIssueUpdated, setEditMode],
	);

	const handleFailure = useCallback(() => {
		setEditMode(false);
	}, [setEditMode]);

	if (field == null) {
		return null;
	}

	return (
		<CellWrapper isEditable>
			{editMode ? (
				<ParentEdit
					parent={parent}
					cellRef={cellRef}
					fieldId={field.fieldId}
					issueKey={issue.key}
					issueId={issue.issueId}
					preferredWidth={preferredWidth}
					onChange={handleChange}
					onFailure={handleFailure}
					readContent={<ParentRead parent={parent} />}
				/>
			) : (
				<ParentRead parent={parent} />
			)}
		</CellWrapper>
	);
};

export const parentCellHandler = {
	[PARENT_TYPE]: ParentCell,
};
